import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import type { ManagedCollectionDetailOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { BulkModStatusControl } from '@/components/user_catalog/BulkModStatusControl';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import {
  MANAGED_COLLECTION_DEFAULT_TAB,
  MANAGED_COLLECTION_SHOW_TABS,
} from '../constants';
import { ManagedCollectionShowHeader } from './ManagedCollectionShowHeader';
import { ManagedCollectionGeneralTab } from './ManagedCollectionGeneralTab';
import { ManagedCollectionCollectionTab } from './ManagedCollectionCollectionTab';
import { SearchPopularityCard } from '@/components/common/show/SearchPopularityCard';
import { CollectionPublicationsTable } from '@/components/user_catalog/collections/CollectionPublicationsTable';

export const ManagedCollectionShow = () => {
  const { token } = theme.useToken();
  const { query } = useShow<ManagedCollectionDetailOut>({
    resource: 'user_catalog.managed_collections',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle('user_catalog.managed_collections', record?.name);

  const [activeTab, onTabChange] = useShowTab(
    MANAGED_COLLECTION_SHOW_TABS,
    MANAGED_COLLECTION_DEFAULT_TAB
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      canDelete={!record?.is_system}
      canEdit={!record?.is_system}
      headerButtons={({ defaultButtons }) => (
        <>
          {record?.id != null && (
            <BulkModStatusControl
              resource="user_catalog.managed_collections"
              ids={[record.id]}
              onSuccess={() => query.refetch()}
            />
          )}
          {defaultButtons}
        </>
      )}
    >
      {record && (
        <>
          <ManagedCollectionShowHeader
            record={record}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <ManagedCollectionGeneralTab record={record} />
            )}
            {activeTab === 'publications' && (
              <CollectionPublicationsTable
                collectionId={record.id}
                collectionKind={record.collection_kind}
              />
            )}
            {activeTab === 'collection' && (
              <ManagedCollectionCollectionTab record={record} />
            )}
            {activeTab === 'search' && (
              <SearchPopularityCard value={record.search_popularity} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
